import React, { useEffect, useMemo, useState } from "react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { QRCodeSVG } from "qrcode.react";
import { getApiErrorMessage } from "../../store/api/baseApi";
import {
  ReviewRecord,
  ReviewStatus,
  useCreateReviewMutation,
  useDeleteReviewMutation,
  useFetchReviewsQuery,
  useUpdateReviewStatusMutation,
} from "../../store/api/reviewsApi";
import { encryptReviewLink } from "../../utils/reviewLinkCrypto";
import "./admin.css";
import "./ReviewManager.css";

const BASE_REVIEW_URL = `${window.location.origin}/leave-review`;

const renderStars = (rating: number) => {
  const n = Math.max(0, Math.min(5, Math.round(rating)));
  return "★".repeat(n) + "☆".repeat(5 - n);
};

const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const el = document.createElement("textarea");
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
  }
};

type ModalTab = "share" | "submit";

const ShareModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [tab, setTab] = useState<ModalTab>("share");

  /* ── Share tab ──────────────────────────────────────────── */
  const [customerName, setCustomerName] = useState("");
  const [customerCity, setCustomerCity] = useState("");
  const [projectName, setProjectName] = useState("");
  const [encryptedUrl, setEncryptedUrl] = useState(BASE_REVIEW_URL);
  const [copied, setCopied] = useState(false);
  const [encrypting, setEncrypting] = useState(false);

  useEffect(() => {
    if (!customerName.trim() && !customerCity.trim() && !projectName.trim()) {
      setEncryptedUrl(BASE_REVIEW_URL);
      return;
    }
    let cancelled = false;
    setEncrypting(true);
    encryptReviewLink({
      name: customerName.trim(),
      city: customerCity.trim(),
      project: projectName.trim(),
    }).then((token) => {
      if (!cancelled) {
        setEncryptedUrl(`${BASE_REVIEW_URL}?d=${token}`);
        setEncrypting(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [customerName, customerCity, projectName]);

  const handleCopy = async () => {
    await copyToClipboard(encryptedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  /* ── Submit tab ─────────────────────────────────────────── */
  const [subName, setSubName] = useState("");
  const [subCity, setSubCity] = useState("");
  const [subProject, setSubProject] = useState("");
  const [subRating, setSubRating] = useState(0);
  const [subComment, setSubComment] = useState("");
  const [subError, setSubError] = useState("");
  const [subDone, setSubDone] = useState(false);

  const [createReview, { isLoading: isCreating }] = useCreateReviewMutation();
  const [updateStatus, { isLoading: isApproving }] = useUpdateReviewStatusMutation();
  const isBusy = isCreating || isApproving;

  const handleAdminSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubError("");
    if (!subName.trim()) { setSubError("Customer name is required."); return; }
    if (!subCity.trim()) { setSubError("City is required."); return; }
    if (subRating < 1) { setSubError("Please select a rating."); return; }
    if (!subComment.trim()) { setSubError("Comment is required."); return; }

    try {
      const created = await createReview({
        customerName: subName.trim(),
        city: subCity.trim(),
        projectTitle: subProject.trim(),
        rating: subRating,
        comment: subComment.trim(),
        source: "admin",
        status: "pending",
        projectId: null,
      }).unwrap();

      const numericId = (created as any)?.data?.id ?? 0;
      if (numericId) {
        await updateStatus({ id: numericId, status: "approved" }).unwrap();
      }
      setSubDone(true);
    } catch (err) {
      setSubError(getApiErrorMessage(err as FetchBaseQueryError));
    }
  };

  const resetSubmitForm = () => {
    setSubDone(false);
    setSubName(""); setSubCity(""); setSubProject("");
    setSubRating(0); setSubComment(""); setSubError("");
  };

  return (
    <div
      className="modal d-block rm-modal-backdrop"
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label="Review link and submission"
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content rm-modal-content">
          <div className="modal-header border-0 pb-0">
            <h5 className="modal-title fw-bold">
              <i className="bi bi-star-fill me-2 text-warning" />
              Review Management
            </h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close review management dialog"
              onClick={onClose}
            />
          </div>

          <div className="px-4 pt-2">
            <ul className="nav nav-tabs">
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link${tab === "share" ? " active" : ""}`}
                  onClick={() => setTab("share")}
                >
                  <i className="bi bi-share me-1" /> Share Link
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link${tab === "submit" ? " active" : ""}`}
                  onClick={() => setTab("submit")}
                >
                  <i className="bi bi-pencil-square me-1" /> Submit Review
                </button>
              </li>
            </ul>
          </div>

          <div className="modal-body pt-3">

            {/* ── Share Link tab ─────────────────────────────── */}
            {tab === "share" && (
              <>
                <p className="text-muted small mb-3">
                  Generate a secure link (details are encrypted in the URL). Pre-fill customer name
                  and project to personalise it.
                </p>

                <div className="mb-3">
                  <label className="form-label fw-semibold small mb-1" htmlFor="rm-cust-name">
                    Customer Name <span className="text-muted fw-normal">(optional)</span>
                  </label>
                  <input
                    id="rm-cust-name"
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="e.g. Pradeep Kumar"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold small mb-1" htmlFor="rm-cust-city">
                    Customer City <span className="text-muted fw-normal">(optional)</span>
                  </label>
                  <input
                    id="rm-cust-city"
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="e.g. Lucknow"
                    value={customerCity}
                    onChange={(e) => setCustomerCity(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold small mb-1" htmlFor="rm-proj-name">
                    Project Name <span className="text-muted fw-normal">(optional)</span>
                  </label>
                  <input
                    id="rm-proj-name"
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="e.g. Raj Nagar Residential Block"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                  />
                </div>

                <label className="form-label fw-semibold small mb-1" htmlFor="rm-link-url">
                  Review Link{" "}
                  {encrypting && (
                    <span className="text-muted fw-normal">(generating…)</span>
                  )}
                </label>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <input
                    id="rm-link-url"
                    type="text"
                    readOnly
                    aria-label="Encrypted review link URL"
                    title="Encrypted review link URL"
                    className="form-control form-control-sm font-monospace rm-url-input"
                    value={encryptedUrl}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm flex-shrink-0 rm-copy-btn"
                    onClick={handleCopy}
                    disabled={encrypting}
                    aria-label="Copy review link"
                  >
                    <i className={`bi bi-${copied ? "check" : "clipboard"} me-1`} />
                    {copied ? "Copied!" : "Copy"}
                  </button>
                </div>

                <div className="text-center mt-2">
                  <p className="small text-muted mb-2">Scan to open on mobile</p>
                  <div className="rm-qr-wrap">
                    {encrypting ? (
                      <div className="rm-qr-placeholder">
                        <div
                          className="spinner-border spinner-border-sm text-secondary"
                          role="status"
                        >
                          <span className="visually-hidden">Generating QR…</span>
                        </div>
                      </div>
                    ) : (
                      <QRCodeSVG value={encryptedUrl} size={160} />
                    )}
                  </div>
                </div>
              </>
            )}

            {/* ── Submit Review tab ───────────────────────────── */}
            {tab === "submit" && (
              <>
                {subDone ? (
                  <div className="text-center py-3">
                    <i className="bi bi-check-circle-fill text-success rm-done-icon" />
                    <p className="fw-semibold mt-2 mb-1">Review submitted and approved.</p>
                    <p className="text-muted small">
                      It will now appear in the approved reviews list.
                    </p>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm mt-2"
                      onClick={resetSubmitForm}
                    >
                      Add Another
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleAdminSubmit} noValidate>
                    <p className="text-muted small mb-3">
                      Submit a review on behalf of a customer. It will be automatically approved.
                    </p>

                    <div className="mb-2">
                      <label className="form-label fw-semibold small mb-1" htmlFor="rm-sub-name">
                        Customer Name
                      </label>
                      <input
                        id="rm-sub-name"
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Full name"
                        value={subName}
                        onChange={(e) => setSubName(e.target.value)}
                        maxLength={100}
                      />
                    </div>

                    <div className="mb-2">
                      <label className="form-label fw-semibold small mb-1" htmlFor="rm-sub-city">
                        City
                      </label>
                      <input
                        id="rm-sub-city"
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="City"
                        value={subCity}
                        onChange={(e) => setSubCity(e.target.value)}
                        maxLength={100}
                      />
                    </div>

                    <div className="mb-2">
                      <label className="form-label fw-semibold small mb-1" htmlFor="rm-sub-proj">
                        Project{" "}
                        <span className="text-muted fw-normal">(optional)</span>
                      </label>
                      <input
                        id="rm-sub-proj"
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Project name"
                        value={subProject}
                        onChange={(e) => setSubProject(e.target.value)}
                        maxLength={200}
                      />
                    </div>

                    <div className="mb-2">
                      <label className="form-label fw-semibold small mb-1">Rating</label>
                      <div className="d-flex gap-1 align-items-center">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button
                            key={n}
                            type="button"
                            aria-label={`Rate ${n} star${n > 1 ? "s" : ""}`}
                            className={`rm-star-btn${subRating >= n ? " rm-star-btn--active" : ""}`}
                            onClick={() => setSubRating(n)}
                          >
                            <i className={`bi bi-star${subRating >= n ? "-fill" : ""}`} />
                          </button>
                        ))}
                        {subRating > 0 && (
                          <span className="small text-muted ms-1">{subRating}/5</span>
                        )}
                      </div>
                    </div>

                    <div className="mb-3">
                      <label
                        className="form-label fw-semibold small mb-1"
                        htmlFor="rm-sub-comment"
                      >
                        Comment
                      </label>
                      <textarea
                        id="rm-sub-comment"
                        className="form-control form-control-sm"
                        placeholder="Customer's feedback…"
                        rows={3}
                        value={subComment}
                        onChange={(e) => setSubComment(e.target.value)}
                        maxLength={1000}
                      />
                    </div>

                    {subError && (
                      <div className="alert alert-danger py-2 small mb-2">
                        <i className="bi bi-exclamation-triangle-fill me-1" />
                        {subError}
                      </div>
                    )}

                    <button
                      type="submit"
                      className="btn btn-success w-100 btn-sm"
                      disabled={isBusy}
                    >
                      {isBusy ? "Saving…" : "Submit & Approve Review"}
                    </button>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════ */

const ReviewManager = () => {
  const {
    data: reviews = [],
    isLoading,
    isFetching,
    refetch,
    error: fetchError,
  } = useFetchReviewsQuery();
  const [updateStatus, { isLoading: isUpdating }] = useUpdateReviewStatusMutation();
  const [deleteReview, { isLoading: isDeleting }] = useDeleteReviewMutation();

  const [statusFilter, setStatusFilter] = useState<"all" | ReviewStatus>("all");
  const [actionError, setActionError] = useState("");
  const [showModal, setShowModal] = useState(false);

  const filteredReviews = useMemo(() => {
    if (statusFilter === "all") return reviews;
    return reviews.filter((r) => r.status === statusFilter);
  }, [reviews, statusFilter]);

  const counts = useMemo(
    () => ({
      total: reviews.length,
      pending: reviews.filter((r) => r.status === "pending").length,
      approved: reviews.filter((r) => r.status === "approved").length,
      flagged: reviews.filter((r) => r.status === "flagged").length,
    }),
    [reviews]
  );

  const handleStatusChange = async (review: ReviewRecord, status: ReviewStatus) => {
    setActionError("");
    try {
      await updateStatus({ id: review.id, status }).unwrap();
    } catch (err) {
      setActionError(getApiErrorMessage(err as FetchBaseQueryError));
    }
  };

  const handleDelete = async (review: ReviewRecord) => {
    if (!window.confirm(`Delete review from "${review.customerName}"?`)) return;
    setActionError("");
    try {
      await deleteReview(review.id).unwrap();
    } catch (err) {
      setActionError(getApiErrorMessage(err as FetchBaseQueryError));
    }
  };

  const fetchErrorMessage = fetchError
    ? getApiErrorMessage(fetchError as FetchBaseQueryError)
    : "";
  const busy = isUpdating || isDeleting;

  return (
    <section className="admin-page">
      {showModal && (
        <ShareModal onClose={() => { setShowModal(false); refetch(); }} />
      )}

      <div className="container-fluid px-0">
        <div className="rm-card">

          <div className="rm-header">
            <div className="rm-header-left">
              <div className="rm-header-icon">
                <i className="bi bi-star-fill" />
              </div>
              <div>
                <div className="rm-header-title">Review Center</div>
                <div className="rm-header-subtitle">Moderate and approve customer reviews</div>
              </div>
            </div>
            <div className="rm-header-right">
              <button
                type="button"
                className="rm-header-btn"
                aria-label="Share review link or submit review"
                onClick={() => setShowModal(true)}
              >
                <i className="bi bi-share" /> Share / Add
              </button>
              <button
                type="button"
                className="rm-header-btn"
                aria-label="Refresh reviews"
                onClick={() => refetch()}
                disabled={isFetching}
              >
                <i className="bi bi-arrow-clockwise" />
                {isFetching ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>

          <div className="card-body p-4">
            <div className="d-flex flex-wrap gap-2 mb-4">
              {(
                [
                  { key: "all",      label: `${counts.total}    Total`,    cls: "admin-filter-pill--active-dark" },
                  { key: "pending",  label: `${counts.pending}  Pending`,  cls: "admin-filter-pill--active-warn" },
                  { key: "approved", label: `${counts.approved} Approved`, cls: "admin-filter-pill--active-success" },
                  { key: "flagged",  label: `${counts.flagged}  Flagged`,  cls: "admin-filter-pill--active-danger" },
                ] as const
              ).map(({ key, label, cls }) => (
                <button
                  key={key}
                  type="button"
                  className={`admin-filter-pill${statusFilter === key ? ` ${cls}` : ""}`}
                  onClick={() => setStatusFilter(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            {fetchErrorMessage && (
              <div className="alert alert-danger d-flex align-items-center gap-2">
                <i className="bi bi-exclamation-triangle-fill" />
                <span>{fetchErrorMessage}</span>
              </div>
            )}
            {actionError && (
              <div className="alert alert-danger d-flex align-items-center gap-2">
                <i className="bi bi-exclamation-triangle-fill" />
                <span>{actionError}</span>
              </div>
            )}

            {isLoading && (
              <div className="alert alert-light border">Loading reviews...</div>
            )}

            {!isLoading && !fetchErrorMessage && (
              filteredReviews.length === 0 ? (
                <div className="alert alert-light border mb-0">
                  No reviews match the current filter.
                </div>
              ) : (
                <div className="admin-review-grid">
                  {filteredReviews.map((review) => (
                    <article className="admin-review-card" key={review.rawId}>
                      <div className="admin-card-meta">
                        <span
                          className={`badge rounded-pill px-2 py-1 ${
                            review.status === "approved"
                              ? "text-bg-success"
                              : review.status === "flagged"
                              ? "text-bg-danger"
                              : "text-bg-warning"
                          }`}
                        >
                          {review.status}
                        </span>
                        <span className="text-muted small">{review.source || "Website"}</span>
                      </div>
                      <h3 className="admin-card-title">{review.customerName || "Anonymous"}</h3>
                      {review.city && (
                        <p className="text-muted small mb-0">{review.city}</p>
                      )}
                      <p className="text-muted small mb-0">
                        {review.projectTitle || "Unassigned project"}
                      </p>
                      <p
                        className="admin-review-stars mb-0"
                        aria-label={`Rated ${review.rating} out of 5`}
                      >
                        {renderStars(review.rating)}
                      </p>
                      <p className="admin-card-copy">{review.comment || "—"}</p>

                      <label
                        className="form-label fw-semibold small mb-1"
                        htmlFor={`status-${review.id}`}
                      >
                        Moderation Status
                      </label>
                      <select
                        id={`status-${review.id}`}
                        className="form-select form-select-sm"
                        value={review.status}
                        disabled={busy}
                        onChange={(e) =>
                          handleStatusChange(review, e.target.value as ReviewStatus)
                        }
                      >
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="flagged">Flagged</option>
                      </select>

                      <div className="mt-2">
                        <button
                          type="button"
                          className="btn btn-outline-danger admin-icon-btn"
                          onClick={() => handleDelete(review)}
                          disabled={busy}
                          title="Delete review"
                          aria-label={`Delete review from ${review.customerName}`}
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReviewManager;
